/**
 * 🔒 useBodyLock — Hook pour verrouiller le scroll du body
 * 
 * Utilisé pour empêcher le scroll du body quand un modal/drawer est ouvert.
 * Préserve la position de scroll lors du déverrouillage.
 * 
 * Compatible iOS Safari, Chrome Android, et desktop.
 * 
 * @param {boolean} isLocked - Si true, le body scroll est verrouillé
 * 
 * @example
 * const [isModalOpen, setIsModalOpen] = useState(false);
 * useBodyLock(isModalOpen);
 */

import { useEffect, useRef } from 'react';

export const useBodyLock = (isLocked) => {
  const scrollPositionRef = useRef(0);
  
  useEffect(() => {
    if (isLocked) {
      // Sauvegarder la position de scroll actuelle
      scrollPositionRef.current = window.scrollY;
      
      // Verrouiller le body
      const scrollY = scrollPositionRef.current;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      // iOS Safari fix: empêcher le touchmove sur le body
      const preventScroll = (e) => {
        // Permettre le scroll à l'intérieur des éléments scrollables
        if (e.target.closest('[data-scroll-lock-ignore]')) {
          return;
        }
        // Bloquer le scroll du body
        if (e.target === document.body || e.target === document.documentElement) {
          e.preventDefault();
        }
      };
      
      document.addEventListener('touchmove', preventScroll, { passive: false });
      
      return () => {
        // Restaurer l'état du body
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        
        // Restaurer la position de scroll
        window.scrollTo(0, scrollPositionRef.current);
        
        // Nettoyer le listener
        document.removeEventListener('touchmove', preventScroll);
      };
    }
  }, [isLocked]);
};

export default useBodyLock;
